import axios from "axios";

const dataServiceEndPoint = process.env.REACT_APP_DATA_RS_ROOT;
const geologyEndPoint = `${dataServiceEndPoint}/api/v1/geology`;

export interface IpdbField {
  name: string;
  displayName: string;
  unit: string;
  sumOrAvg: string;
  min: number;
  max: number;
  bin: number;
  tooltip: string;
}

export interface IpdbSourcePlay {
  source: string;
  plays: string[];
  userList: string[];
}

export interface CutoffSource {
  source: string;
  originalSource: string;
  cutoff: string;
  cutoffStatus: string;
}

const fetchPlayTo3DDataSources = async () => {
  const result = await axios.get<string[]>(`${geologyEndPoint}/ipdb/playto3d/sources`);

  return result.data;
};

const fetchOriginalPlayTo3DDataSources = async () => {
  const result = await axios.get<string[]>(
    `${geologyEndPoint}/ipdb/playto3d/original-sources`
  );

  return result.data;
};

const fetchPlayTo3DDataPrivateSources = async () => {
  const result = await axios.get<IpdbSourcePlay[]>(
    `${geologyEndPoint}/ipdb/playto3d/sources/private`
  );

  return result.data;
};

const fetchAllPlayTo3DDataPrivateSources = async () => {
  const result = await axios.get<IpdbSourcePlay[]>(
    `${geologyEndPoint}/ipdb/playto3d/sources/admin`
  );

  return result.data;
};

const fetchPlayTo3DCutoffSources = async () => {
  const result = await axios.get<CutoffSource[]>(
    `${geologyEndPoint}/ipdb/playto3d/sources/cutoff`
  );

  return result.data;
};

async function fetchRadiusDimensions(uwis) {
  const url = `${geologyEndPoint}/ipdb/xdaDimensions`;
  const response = await axios.post(url, uwis);
  return response.data;
}

async function fetchDimensionsFromPlay(play) {
  const url = `${geologyEndPoint}/ipdb/xdaDimensions/${play}`;
  const response = await axios.get(url);
  return response.data;
}

const fetchPlayTo3DFields = async (source: string) => {
  const result = await axios.get<IpdbField[]>(
    `${geologyEndPoint}/ipdb/playto3d/${source}/fields`
  );

  return result.data;
};

const fetchPlayZoneFields = async (modelSource: string, zones: string[]) => {
  if (!zones || !zones.length) {
    return [];
  }

  const result = await axios.post<IpdbField[]>(
    `${geologyEndPoint}/ipdb/playzone/${encodeURIComponent(modelSource)}/fields`,
    zones
  );

  return result.data;
};

const updateSourceAccessUsers = async (source: string, userList: string[]) => {
  const sourceEncoded = encodeURIComponent(source);

  const result = await axios.put(
    `${geologyEndPoint}/ipdb/playto3d/sources/${sourceEncoded}/access`,
    userList
  );

  return result.data;
};

// ---------------------------------------------------------------------------
// Play radius defaults — admin-configurable per-play XDA dimension presets.
// TODO: connect to real backend endpoint once available.
// ---------------------------------------------------------------------------

export interface PlayRadiusDimension {
  name: string;
  [key: string]: number | string;
}

export interface PlayRadiusDefault {
  play: string;
  dimensions: PlayRadiusDimension[];
}

interface PlayRadiusDefaultResponse {
  name: string;
  config: {
    xdaCalculationDimensions: PlayRadiusDimension[];
  };
}

const mapPlayRadiusDefault = (r: PlayRadiusDefaultResponse): PlayRadiusDefault => ({
  play: r.name,
  dimensions: r.config?.xdaCalculationDimensions ?? []
});

const PLAY_DEFAULTS_ENDPOINT = `${process.env.REACT_APP_DATA_RS_ROOT}/api/v1/play-defaults`;

const fetchPlayRadiusDefaults = async (): Promise<PlayRadiusDefault[]> => {
  const response = await axios.get<PlayRadiusDefaultResponse[]>(
    `${PLAY_DEFAULTS_ENDPOINT}/plays`
  );
  return response.data.map(mapPlayRadiusDefault);
};

const createPlayRadiusDefault = async (
  entries: PlayRadiusDefault[]
): Promise<PlayRadiusDefault[]> => {
  const response = await axios.post<PlayRadiusDefaultResponse[]>(
    `${PLAY_DEFAULTS_ENDPOINT}/plays`,
    entries.map((e) => ({
      play: e.play,
      config: { xdaCalculationDimensions: e.dimensions }
    })),
    { headers: { "Content-Type": "application/json" } }
  );
  return response.data.map(mapPlayRadiusDefault);
};

const savePlayRadiusDefault = async (
  entry: PlayRadiusDefault
): Promise<PlayRadiusDefault[]> => {
  const response = await axios.put<PlayRadiusDefaultResponse[]>(
    `${PLAY_DEFAULTS_ENDPOINT}/play`,
    { play: entry.play, config: { xdaCalculationDimensions: entry.dimensions } },
    { headers: { "Content-Type": "application/json" } }
  );
  return response.data.map(mapPlayRadiusDefault);
};

const deletePlayRadiusDefault = async (play: string): Promise<void> => {
  await axios.delete(`${PLAY_DEFAULTS_ENDPOINT}/play`, {
    headers: { "Content-Type": "application/json" },
    data: { play }
  });
};

export {
  fetchPlayTo3DFields,
  fetchPlayZoneFields,
  fetchPlayTo3DDataSources,
  fetchPlayTo3DDataPrivateSources,
  fetchRadiusDimensions,
  fetchDimensionsFromPlay,
  fetchPlayTo3DCutoffSources,
  fetchOriginalPlayTo3DDataSources,
  fetchAllPlayTo3DDataPrivateSources,
  updateSourceAccessUsers,
  fetchPlayRadiusDefaults,
  createPlayRadiusDefault,
  savePlayRadiusDefault,
  deletePlayRadiusDefault
};
